import { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import {
	IAddEditTableForm,
	compareDateFields,
	APP_CONFIG,
	manageResourceIds,
	moneyFormat,
} from "../../../../../../helpers/appHelpers";
import useEditFormStore from "../../../../../../state-management/useEditFormStore";
import {
	useCreateCompanyTaxCode,
	useSetDefaultTaxCode,
	useUpdateCompanyTaxCode,
} from "../../../../../../services/company-tax-code.service";
import ModalComponent from "../../../../../../components/modals/ModalFormComponent";
import Label from "../../../../../../components/form_fields/Label";
import { useGetCompanyTaxRate } from "../../../../../../services/company-tax-rate.service";
import { useGetCompanyTaxRelief } from "../../../../../../services/company-tax-relief.service";
import { useGetCompanyBenefitTax } from "../../../../../../services/company-benefit-tax.service";
import { useManageTableWithSearchParamsData } from "../../../../../../helpers/useManageTable";
import MultiSelectField from "../../../../../../components/form_fields/MultiSelectField";
import { PATHS } from "../../../../../../routes/routes.paths";
import OrangeButton from "../../../../../../components/buttons/OrangeButton";

interface IFormInputs {
	startDate: string;
	endDate: string;
	enabled: string;
	isDefault: string;
	effectiveDate: string;
	expiryDate: string;
	addTaxRateIds?: number[];
	removeTaxRateIds?: number[];
	addBenefitTaxIds?: number[];
	removeBenefitTaxIds?: number[];
	addTaxReliefIds?: number[];
	removeTaxReliefIds?: number[];
}

interface CompanyTaxCodeModalFormProps extends IAddEditTableForm {
	companyId: number;
	reloadTableData?: () => void;
}

const CompanyTaxCodeModalForm = ({
	open,
	setOpen,
	title,
	isEditForm,
	tableAPIRef,
	companyId,
	reloadTableData,
}: CompanyTaxCodeModalFormProps) => {
	const { formDataValues } = useEditFormStore();
	const [uploading, setUploading] = useState(false);
	const [defaultCode, setDefaultCode] = useState(false);

	useEffect(() => {
		if (isEditForm) setDefaultCode(formDataValues?.isDefault);
	}, [formDataValues]);

	const {
		register,
		watch,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm<IFormInputs>({
		defaultValues: {
			startDate: isEditForm ? formDataValues?.startDate?.slice(0, 10) : "",
			endDate: isEditForm ? formDataValues?.endDate?.slice(0, 10) : "",
			enabled: isEditForm ? (formDataValues?.enabled ? "yes" : "no") : "",
			isDefault: isEditForm ? (formDataValues?.isDefault ? "yes" : "no") : "",
			effectiveDate:
				isEditForm && formDataValues?.isDefault
					? formDataValues?.effectiveDate?.slice(0, 10)
					: undefined,
			expiryDate:
				isEditForm && formDataValues?.isDefault
					? formDataValues?.expiryDate?.slice(0, 10)
					: undefined,
		},
	});

	const queryOptions = {
		companyId,
		limit: APP_CONFIG.PAGE_LIMIT,
		page: 1,
		orderBy: "createdAt:desc" as any,
	};

	const { data: taxRateRules } = useGetCompanyTaxRate(companyId, queryOptions);
	const { data: benefitTaxRules } = useGetCompanyBenefitTax(
		companyId,
		queryOptions,
	);
	const { data: reliefTaxRules } = useGetCompanyTaxRelief(
		companyId,
		queryOptions,
	);

	const [taxRateIds, setTaxRateIds] = useState<number[]>([]);
	const [benefitTaxIds, setBenefitTaxIds] = useState<number[]>([]);
	const [taxReliefIds, setTaxReliefIds] = useState<number[]>([]);

	const { mutate: createTaxCode } = useCreateCompanyTaxCode();
	const { mutate: updateTaxCode } = useUpdateCompanyTaxCode();
	const { mutate: setDefaultTaxCode } = useSetDefaultTaxCode();
	const { addNewTableData } = useManageTableWithSearchParamsData();

	const onSubmit: SubmitHandler<IFormInputs> = (data: any) => {
		setUploading(true);
		data.enabled = data.enabled === "yes";
		data.isDefault = data.isDefault === "yes";

		if (isEditForm) {
			const { addIds: addTaxRateIds, removeIds: removeTaxRateIds } =
				manageResourceIds({
					newData: taxRateIds,
					oldData:
						formDataValues?.rateRules?.map((r: any) => r?.taxRate.id) || [],
				});
			const { addIds: addBenefitTaxIds, removeIds: removeBenefitTaxIds } =
				manageResourceIds({
					newData: benefitTaxIds,
					oldData:
						formDataValues?.benefitRules?.map((r: any) => r?.benefitTax.id) ||
						[],
				});
			const { addIds: addTaxReliefIds, removeIds: removeTaxReliefIds } =
				manageResourceIds({
					newData: taxReliefIds,
					oldData:
						formDataValues?.reliefRules?.map((r: any) => r?.taxRelief.id) || [],
				});

			updateTaxCode(
				{
					companyId,
					id: formDataValues?.id,
					payload: {
						enabled: data.enabled,
						startDate: data.startDate,
						endDate: data.endDate,
						addTaxRateIds,
						removeTaxRateIds,
						addBenefitTaxIds,
						removeBenefitTaxIds,
						addTaxReliefIds,
						removeTaxReliefIds,
					},
				},
				{
					onSuccess: (updatedData) => {
						if (defaultCode) {
							setDefaultTaxCode(
								{
									companyId,
									id: formDataValues?.id,
									payload: {
										effectiveDate: data.effectiveDate,
										expiryDate: data.expiryDate,
									},
								},
								{
									onSuccess: (defaultTaxCodeData) => {
										try {
											tableAPIRef?.current.updateRows([updatedData.data]);
										} catch (error) {
											console.error("Error updating table rows:", error);
											reloadTableData?.();
										}
										setUploading(false);
										setOpen(false);
										reset();
									},
									onError: () => setUploading(false),
								},
							);
						} else {
							try {
								tableAPIRef?.current.updateRows([updatedData.data]);
							} catch {
								reloadTableData?.();
							}
							setUploading(false);
							setOpen(false);
							reset();
						}
					},
					onSettled: () => setUploading(false),
				},
			);
		} else {
			createTaxCode(
				{
					companyId,
					payload: {
						...data,
						taxRateIds,
						benefitTaxIds,
						taxReliefIds,
					},
				},
				{
					onSuccess: () => {
						addNewTableData();
						setOpen(false);
						reset();
					},
					onSettled: () => setUploading(false),
				},
			);
		}
	};

	return (
		<ModalComponent
			width={700}
			open={open}
			title={title}
			handleClose={() => setOpen(false)}
		>
			<form onSubmit={handleSubmit(onSubmit)}>
				<div className="grid grid-cols-2 gap-x-4 gap-y-6">
					<div className="w-full">
						<Label title="Start Date" for="startDate" />
						<input
							defaultValue={
								isEditForm ? formDataValues?.startDate?.slice(0, 10) : ""
							}
							placeholder="mm/dd/yyyy"
							type="date"
							{...register("startDate", { required: "Enter start date here" })}
							id="startDate"
							className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm ${
								errors.startDate
									? "border border-red-500 focus:border-red-500 focus:outline-red-500"
									: "border-none"
							}`}
						/>
						{errors.startDate && (
							<p className="text-red-500 text-sm mt-1">
								{errors.startDate.message}
							</p>
						)}
					</div>
					<div className="w-full">
						<Label title="End Date" for="endDate" />
						<input
							defaultValue={
								isEditForm ? formDataValues?.endDate?.slice(0, 10) : ""
							}
							placeholder="mm/dd/yyyy"
							type="date"
							{...register("endDate", {
								required: "Enter end date here",
								validate: (value) =>
									compareDateFields(watch("startDate"), value),
							})}
							id="endDate"
							className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm ${
								errors.endDate
									? "border border-red-500 focus:border-red-500 focus:outline-red-500"
									: "border-none"
							}`}
						/>
						{errors.endDate && (
							<p className="text-red-500 text-sm mt-1">
								{errors.endDate.message}
							</p>
						)}
					</div>

					<div className="w-full">
						<Label title="Tax Rate Rules" for="taxRateIds" optional />
						<MultiSelectField
							key="taxRateIds"
							options={taxRateRules?.data || []}
							setResourceIds={setTaxRateIds}
							placeholder="Select Options"
							customMapText={(option) =>
								`${option?.id}: [Min tax (${moneyFormat(option?.minTaxableAmount)}), Max tax (${moneyFormat(option?.maxTaxableAmount)}), Rate (${option?.rateComponent * 100}%)]`
							}
							pathname={PATHS.SUB_COMPANY_SETTINGS.TAX_RULES + "&taxRule=rate"}
							required={false}
							defaultValues={
								!isEditForm
									? []
									: formDataValues?.rateRules?.map((r: any) => r?.taxRate.id) ||
										[]
							}
							useKeyDisplay
						/>
					</div>
					<div className="w-full">
						<Label title="Benefit Tax Rules" for="benefitTaxIds" optional />
						<MultiSelectField
							key="benefitTaxIds"
							options={benefitTaxRules?.data || []}
							setResourceIds={setBenefitTaxIds}
							placeholder="Select Options"
							customMapText={(option) =>
								`${option?.id}: [Min tax (${moneyFormat(option?.minTaxableAmount)}), Max tax (${moneyFormat(option?.maxTaxableAmount)}), Rate (${option?.rateComponent * 100}%)]`
							}
							pathname={
								PATHS.SUB_COMPANY_SETTINGS.TAX_RULES + "&taxRule=benefit"
							}
							required={false}
							defaultValues={
								!isEditForm
									? []
									: formDataValues?.benefitRules?.map(
											(r: any) => r?.benefitTax.id,
										) || []
							}
							useKeyDisplay
						/>
					</div>
					<div className="w-full">
						<Label title="Relief Tax Rules" for="taxReliefIds" optional />
						<MultiSelectField
							key="taxReliefIds"
							options={reliefTaxRules?.data || []}
							setResourceIds={setTaxReliefIds}
							placeholder="Select Options"
							customMapText={(option) =>
								`${option?.id}: [Tax relief type (${option?.taxReliefType?.name}), Rate (${option?.rateComponent * 100}%)]`
							}
							pathname={
								PATHS.SUB_COMPANY_SETTINGS.TAX_RULES + "&taxRule=relief"
							}
							required={false}
							defaultValues={
								!isEditForm
									? []
									: formDataValues?.reliefRules?.map(
											(r: any) => r?.taxRelief.id,
										) || []
							}
							useKeyDisplay
						/>
					</div>

					<div className="col-span-2">
						<div className="flex space-x-12 text-base">
							<h3>Enabled</h3>
							<div className="flex space-x-3 items-center">
								<input
									{...register("enabled")}
									defaultChecked={
										isEditForm && formDataValues?.enabled === true
									}
									type="radio"
									value="yes"
									id="enabled"
								/>
								<label htmlFor="enabled">Yes</label>
							</div>
							<div className="flex space-x-3 items-center">
								<input
									{...register("enabled")}
									defaultChecked={
										isEditForm && formDataValues?.enabled === false
									}
									type="radio"
									value="no"
									id="inenabled"
								/>
								<label htmlFor="inenabled">No</label>
							</div>
						</div>
					</div>
					<div className="col-span-2">
						<div className="flex space-x-12 text-base">
							<h3>Default</h3>
							<div className="flex space-x-3 items-center">
								<input
									{...register("isDefault")}
									defaultChecked={
										isEditForm && formDataValues?.isDefault === true
									}
									type="radio"
									value="yes"
									id="isDefault"
									onChange={() => setDefaultCode(true)}
								/>
								<label htmlFor="isDefault">Yes</label>
							</div>
							<div className="flex space-x-3 items-center">
								<input
									{...register("isDefault")}
									defaultChecked={
										isEditForm && formDataValues?.isDefault === false
									}
									type="radio"
									value="no"
									id="notDefault"
									onChange={() => setDefaultCode(false)}
								/>
								<label htmlFor="notDefault">No</label>
							</div>
						</div>
					</div>

					{defaultCode && (
						<>
							<div className="w-full">
								<Label title="Effective Date" for="effectiveDate" />
								<input
									defaultValue={
										isEditForm
											? formDataValues?.effectiveDate?.slice(0, 10)
											: ""
									}
									placeholder="mm/dd/yyyy"
									type="date"
									{...register("effectiveDate", {
										required: "Enter effective date here",
									})}
									id="effectiveDate"
									className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm ${
										errors.effectiveDate
											? "border border-red-500 focus:border-red-500 focus:outline-red-500"
											: "border-none"
									}`}
								/>
								{errors.effectiveDate && (
									<p className="text-red-500 text-sm mt-1">
										{errors.effectiveDate.message}
									</p>
								)}
							</div>
							<div className="w-full">
								<Label title="Expiry Date" for="expiryDate" />
								<input
									defaultValue={
										isEditForm ? formDataValues?.expiryDate?.slice(0, 10) : ""
									}
									placeholder="mm/dd/yyyy"
									type="date"
									{...register("expiryDate", {
										required: "Enter expiry date here",
									})}
									id="expiryDate"
									className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm ${
										errors.expiryDate
											? "border border-red-500 focus:border-red-500 focus:outline-red-500"
											: "border-none"
									}`}
								/>
								{errors.expiryDate && (
									<p className="text-red-500 text-sm mt-1">
										{errors.expiryDate.message}
									</p>
								)}
							</div>
						</>
					)}
				</div>

				<div className="w-full flex justify-end mt-8">
					<OrangeButton
						type="submit"
						title={
							uploading
								? "Uploading..."
								: isEditForm
									? "Update Tax Code"
									: "Add Tax Code"
						}
						className=""
						disabled={uploading}
					/>
				</div>
			</form>
		</ModalComponent>
	);
};

export default CompanyTaxCodeModalForm;
